import React, { FC, useEffect, useMemo, useState } from 'react'

import { checkPollinationPanel, getHost, useSendMessage } from '../hooks'
import { GeometryAction } from '../hooks/types'

import { Action, SendGeometryProps } from './SendGeometry.types'
import { ArrowRepeat, BuildingAdd, Eyeglasses, Recycle, Trash } from 'react-bootstrap-icons'
import { BaseDropdown, Option } from '../atoms/BaseDropdown/BaseDropdown'
import { Button } from '../atoms'

const getAction = (key: string): GeometryAction => {
  switch (key) {
    case 'add':
      return 'AddGeometry'
    case 'delete':
      return 'DeleteGeometry'
    case 'preview':
      return 'PreviewGeometry'
    case 'clear':
      return 'ClearGeometry'
    case 'subscribe-preview':
      return 'SubscribePreviewGeometry'
  }
}

const overlayOptions: Option[] = [
  {
    type: 'label',
    id: Action.preview,
    label: 'Preview',
    icon: <Eyeglasses size={16} />,
  },
  {
    type: 'label',
    id: Action.add,
    label: 'Add',
    icon: <BuildingAdd size={16} />,
  },
  {
    type: 'label',
    id: Action.delete,
    label: 'Delete',
    icon: <Trash size={16} />,
  },
  {
    type: 'label',
    id: Action.clear,
    label: 'Clear',
    icon: <Recycle size={16} />,
  },
  {
    type: 'label',
    id:  Action.subscribePreview,
    label: 'Subscribe Preview',
    icon: <ArrowRepeat size={16} />,
  },
] 

export const SendGeometry: FC<SendGeometryProps> = ({
  geometry,
  defaultAction,
  defaultKey,
  optionsConfig,
  geometryOptions,
  onOverlayHeightChange = (height: number) => undefined,
}) => {
  const [key, setKey] = useState(defaultKey ?? performance.now().toString())
  const [currOption, setCurrOption] = useState<Option>()
  const onSelect = (o: any) => setCurrOption(o)

  const host = getHost()
  const { sendMessage } = useSendMessage()

  const disabled = useMemo(() => {
    return !checkPollinationPanel()    
  }, [])

  const values = useMemo(() => {
    if (!optionsConfig) return overlayOptions

    return overlayOptions.filter(v => optionsConfig[v.id] === true || optionsConfig[v.id] === undefined)
  }, [optionsConfig])

  useEffect(() => {
    const defaultVal = overlayOptions.filter(v => v.id === defaultAction)
    if (!defaultVal) return

    setCurrOption(defaultVal[0])
  }, [defaultAction])

  useEffect(() => {
    setKey(defaultKey)
  }, [defaultKey])

  useEffect(() => {
    if (disabled || currOption?.id !== 'subscribe-preview') return

    sendMessage(
      'SubscribePreviewGeometry',
      {
        data: geometry,
        uniqueId: key,
        options: geometryOptions
      },
    )

    return () => {
      sendMessage(
        'ClearGeometry',
        {
          data: geometry,
          uniqueId: key,
          options: geometryOptions
        },
      )
    }
  }, [disabled, geometry, geometryOptions, key, currOption, sendMessage])

  const helpTextButton = {
    add: `Add geometry to the ${host} viewport`,
    preview: `Preview geometry in the ${host} viewport`,
    delete: `Delete geometry from the ${host} viewport`,
    clear: `Clear geometry from the ${host} viewport`,
    'subscribe-preview': `Render geometry in the ${host} viewport`,
  }

  return <BaseDropdown 
    onOverlayHeightChange={onOverlayHeightChange}
    childOverlayStyle = {{ 
      width: 'auto', 
      backgroundColor: 'white',
      color: '#393e46',
      fontSize: '12px',
      padding: '10px',
      cursor: 'pointer'
    }}
    triggerStyle={{
      border: 'solid 1px #fff'
    }}
    placement={'bottomRight'}
    activeId={defaultAction}
    disabled={disabled}
    action={'click'}
    onSelect={onSelect}
    options={values}>
    <Button
      title={helpTextButton[currOption?.id]}
      disabled={disabled}
      style={{ 
        width: '100%', 
        fontSize: '14px',
        border: 'solid 1px #fff'
      }}
      onClick={(e: any) => {
        e.stopPropagation()
        if (disabled) return

        sendMessage(
          getAction(currOption?.id),
          {
            data: geometry,
            uniqueId: key,
            options: geometryOptions
          },
        )
      }}
    >
      {!disabled && <span
        style={{
          flexShrink: 0,
          display: 'flex'
        }}>{currOption?.icon}</span>}
      {!disabled && (currOption?.label ?? 'Pollination')}
      {disabled && <span
        style={{ color: 'red' }}
      >Enabled in Pollination CAD plugins</span>}
    </Button>
  </BaseDropdown>
}
